'use client';
import { buildProductsRequest } from '@eliadmin/core/src/api/products/product-list-request';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import { type ProductCardProps } from '@eliadmin/core/src/components/product/product-card';
import { ProductsGrid } from '@eliadmin/core/src/components/product/products-grid';
import { Pagination } from '@eliadmin/core/src/components/ui/pagination';
import { Skeleton } from '@eliadmin/core/src/components/ui/skeleton';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalNavigation, useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import {
  buildProductsQuery,
  parseProductSearchParams,
  type BuildProductQueryOptions,
} from '@eliadmin/core/src/utils/product-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';

export type ProductsGridContainerProps = {
  queryOpts?: BuildProductQueryOptions;
  className?: {
    wrapper?: string;
    product?: ProductCardProps['className'];
  };
};
export function ProductsGridContainer(props: ProductsGridContainerProps) {
  const locale = useLocaleStore(s => s.currentLocale);
  const { t } = useTranslations();
  const isGlobalNavigating = useGlobalNavigation();
  const { me } = useCustomerStore();

  const searchParams = useSearchParams();
  const searchQueryOpts = parseProductSearchParams(searchParams);

  const queryWithoutPage = buildProductsQuery({
    ...searchQueryOpts,
    ...props.queryOpts,
    showAttributes: true,
  });

  const productRequestUri = buildProductsRequest({ ...queryWithoutPage, results: 12 }).uri;
  const productResetUri = buildProductsRequest({
    ...{ ...queryWithoutPage, results: 12 },
    page: undefined,
  }).uri;

  const { data: products } = useSuspenseQuery({
    queryKey: ['products', locale, productRequestUri, me?.authToken ?? null],
    queryFn: () =>
      fetchProducts({
        locale,
        query: { ...queryWithoutPage, results: 12 },
        authToken: me?.authToken,
      }),
  });

  return (
    <div className={cn('flex flex-col gap-8 sm:gap-10 lg:gap-12', 'ea-products-grid-container')}>
      {!products?.data?.length && (
        <p className="text-center text-muted-foreground">{t('text554')}</p>
      )}

      <ProductsGrid products={products?.data ?? []} />

      <div className={cn('flex flex-row justify-center', 'ea-products-grid-container__pagination')}>
        <Pagination links={products?.meta.links} resetWhen={[productResetUri]} />
      </div>

      {!!isGlobalNavigating && (
        <div
          className={cn(
            'absolute inset-0 z-10 bg-background/30',
            'ea-products-grid-container__loading-overlay',
          )}
        ></div>
      )}
    </div>
  );
}

export function ProductsGridContainerSkeleton() {
  const templateSettings = useGlobalStore(s => s.templateSettings);

  const array = Array.from({ length: 6 }, (_, i) => i);

  return (
    <div
      className={cn('flex flex-col gap-8 @container', 'ea-products-grid-container-skeleton', {
        'col-span-12': !templateSettings.category?.left_filter?.enabled,
      })}
    >
      <div className={cn('grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4')}>
        {array.map(i => (
          <Skeleton key={i} className="min-h-[400px] min-w-[250px]" />
        ))}
      </div>

      <Skeleton className="h-20 w-full" />
    </div>
  );
}
